import { jsx as _jsx } from "react/jsx-runtime";
import { CircularProgress } from "@mui/material";
import { applyTokens } from "../../../utilities/applyTokens.js";
import { createLazyIcon } from "../../../utilities/createLazyIcon.js";
import { CircularProgressPresets } from "./loadingIndicator.js";
/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} string - The input string.
 * @returns {string} The input string with the first letter capitalized.
 */
function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}
/**
 * Generates alerts variants styles based on the provided theme tokens.
 *
 * @param tokens - The theme tokens used to generate the alerts variants styles.
 * @returns An object containing the generated alerts variants styles.
 */
function generateAlerts(tokens) {
    const { alert } = tokens.component;
    const alertType = [
        "error",
        "info",
        "success",
        "warning",
        "processing",
    ];
    return alertType.reduce((acc, alertType) => (Object.assign(Object.assign({}, acc), { [`standard${capitalizeFirstLetter(alertType)}`]: Object.assign({}, applyTokens(alert[alertType])), [`outlined${capitalizeFirstLetter(alertType)}`]: Object.assign({}, applyTokens(alert[alertType])), [`divided${capitalizeFirstLetter(alertType)}`]: Object.assign({}, applyTokens(alert[alertType])) })), {});
}
const alert = (tokens) => {
    const { alert } = tokens.component;
    const ErrorIcon = createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Error.js"));
    const InfoIcon = createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Info.js"));
    const SuccessIcon = createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Success.js"));
    const WarningIcon = createLazyIcon(() => import("@diligentcorp/atlas-react-icons/dist/esm/lens/Warning.js"));
    return {
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    error: _jsx(ErrorIcon, {}),
                    info: _jsx(InfoIcon, {}),
                    success: _jsx(SuccessIcon, {}),
                    warning: _jsx(WarningIcon, {}),
                    processing: (_jsx(CircularProgress, Object.assign({ variant: "indeterminate", value: 75 }, CircularProgressPresets(tokens).size.md))),
                },
            },
            styleOverrides: Object.assign(Object.assign({}, generateAlerts(tokens)), { 
                // defaults
                root: Object.assign(Object.assign({}, applyTokens(alert)), { border: "none", alignItems: "center", boxShadow: "none", display: "grid", gridTemplateColumns: "auto 1fr auto", gridTemplateRows: "auto", justifyContent: "flex-start", gridTemplateAreas: `
          "col1 col2 col3"
        ` }), icon: {
                    margin: 0,
                    opacity: 1,
                    gridArea: "col1",
                    "& > svg": Object.assign({}, applyTokens(alert.icon)),
                }, message: Object.assign({ gridArea: "col2", flexGrow: 1 }, applyTokens(alert.message)), action: Object.assign(Object.assign({ gridArea: "col3", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 0, margin: 0 }, applyTokens(alert.action)), { "& .MuiButton-root": {
                        backgroundColor: "transparent",
                    } }), 
                // Divided: Atlas Toast with actions on a second row
                divided: {
                    display: "grid",
                    gridTemplateColumns: "40px auto",
                    gridTemplateRows: "auto auto",
                    gridTemplateAreas: `
                  "col1 col2"
                  "col1 col3"
                `,
                    "& .MuiAlert-icon": {
                        gridArea: "col1",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    },
                    "& .MuiAlert-message": Object.assign(Object.assign({}, applyTokens(alert.divided.message)), { gridArea: "col2" }),
                    "& .MuiAlert-action": Object.assign(Object.assign({}, applyTokens(alert.divided.action)), { gridArea: "col3", display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }),
                } }),
        },
    };
};
export default alert;
//# sourceMappingURL=alert.js.map